import {Button, CircularProgress, Stack, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {finalize} from 'rxjs';

import {Indexation, IndexationRate, indexationService, ManageIndexationRate} from '@store/settings/indexation';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import dayjs from '@utils/dayjs.utils';

import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';

interface IndexationRatesHistoryModalProps {
  indexation: Indexation;
  handleClose: () => void;
}

const IndexationRatesHistoryModal = (props: IndexationRatesHistoryModalProps) => {
  const {indexation, handleClose} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [rates, setRates] = useState<IndexationRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showAddRate, setShowAddRate] = useState(false);
  const [newRate, setNewRate] = useState<ManageIndexationRate>({});

  const fetchRates = useCallback(() => {
    indexationService.getIndexation(indexation.id.toString()).subscribe({
      next: (data) => {
        setRates(data.indexationRates ?? []);
        setLoading(false);
      },
      error: (err) => {
        enqueueSnackbar(err.text, err.options);
        setLoading(false);
      },
    });
  }, [indexation.id, enqueueSnackbar]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const updateRate = (name: string) => (value: any) => setNewRate((state) => ({...state, [name]: value}));

  const errors = {
    rateInPercent: getFieldError(newRate.rateInPercent, FieldValidationType.REQUIRED_NUMBER),
    fromDate: getFieldError(newRate.fromDate, FieldValidationType.REQUIRED_STRING),
  };

  const handleSaveRate = () => {
    setSaveLoading(true);
    indexationService.updateRate(indexation.id, newRate).pipe(finalize(() => setSaveLoading(false)))
      .subscribe({
        next: () => {
          enqueueSnackbar(t('settings.indexation.addRate.title'), {variant: 'success'});
          setNewRate({});
          setShowAddRate(false);
          setLoading(true);
          fetchRates();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <ModalComponent
      title={`${indexation.name} — ${t('settings.indexation.drawer.rate.title')}`}
      handleClose={handleClose}
      maxWidth="lg"
      content={(
        <Stack width="100%" spacing={2} pt="55px" pb="40px" height="70vh">
          {loading && (
            <Stack flex={1} alignItems="center" justifyContent="center">
              <CircularProgress/>
            </Stack>
          )}
          {!loading && (
            <NewTableComponent
              columns={[
                COLUMN_TYPES.TEXT({
                  field: 'fromDate',
                  label: t('settings.indexation.addRate.fields.fromDate'),
                  valueGetter: (params) => params.row.fromDate
                    ? dayjs(params.row.fromDate).format('MM.YY')
                    : '-',
                  minWidth: 200,
                  fullWidth: true,
                  editable: false
                }),
                COLUMN_TYPES.NUMBER({
                  field: 'rateInPercent',
                  label: t('settings.indexation.addRate.fields.rateInPercent'),
                  valueGetter: (params: { row: IndexationRate }) => `${params.row.rateInPercent}%`,
                  width: 150,
                  editable: false
                }),
              ]}
              data={rates}
              disablePagination={true}
              disableRowSelection={true}
              disableEdition={true}
            />
          )}
          {showAddRate && (
            <Stack spacing={2} pt="10px">
              <Typography fontSize={16} fontWeight="700">
                {t('settings.indexation.addRate.title')}
              </Typography>
              <Stack width="70%" spacing={2}>
                <NumberTextFieldComponent
                  color="secondary"
                  labelWidth={180}
                  label={t('settings.indexation.addRate.fields.rateInPercent')}
                  placeholder={t('global.writeSomething')}
                  pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
                  endAdornment={<Typography fontSize={16}>%</Typography>}
                  handleChange={updateRate('rateInPercent')}
                  value={newRate.rateInPercent ?? undefined}
                  error={errors.rateInPercent}
                  required/>
                <DatePickerComponent
                  color="primary"
                  labelWidth={180}
                  label={t('settings.indexation.addRate.fields.fromDate')}
                  handleChange={updateRate('fromDate')}
                  value={newRate.fromDate}
                  error={errors.fromDate}
                  required/>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
      actions={(
        <Stack direction="row" spacing={2}>
          {!showAddRate && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowAddRate(true)}>
              {t('settings.indexation.addRate.title')}
            </Button>
          )}
          {showAddRate && (
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setShowAddRate(false);
                  setNewRate({});
                }}>
                {t('global.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={saveLoading || checkIfErrors(errors)}
                onClick={handleSaveRate}>
                {t('global.save')}
              </Button>
            </>
          )}
        </Stack>
      )}
    />
  );
};

export default IndexationRatesHistoryModal;